/**
 * Scorecard Metric Details Drawer - Alpine.js Mixin
 *
 * All logic for the chart-area inside the metric details drawer:
 * data fetching, filtering, delta computation, the sparkline's contract
 * and tooltip, and date/label helpers.
 *
 * The sparkline is drawn by the shared Superset bundle through
 * <x-charts.superset-chart>: the same line the report's Line card draws,
 * built in PHP by Scorecard\View::drawerChart() (metric-cards.md). This
 * module asks for it, picks the picture the goal toggles show and hands it
 * to the host with the app's tooltip. It imports neither echarts nor the
 * bundle; it is reachable from app.js, and the host loads the bundle on the
 * first draw.
 *
 * Prefix: mdd_
 */

import { metricHigherIsBetter } from '../utils';
import { TOOLTIP_CHROME, supersetTooltipFormatter } from '../charts/metric-tooltip';
import { GOAL_BAND_BAD_COLOR, GOAL_BAND_GOOD_COLOR, GOAL_LINE_COLOR } from '../charts/palette';
import { scorecardPeriodNames } from '../charts/scorecard-period-names';

const CHART_DATA_EVENT = 'superset-chart-data';

// Time conversion constant
const MS_PER_DAY = 1000 * 60 * 60 * 24;

// The projection's rule as ScorecardDrawerChartBuilder applies it, so the badge counts the
// projected period exactly when the chart draws it. The badge is worked out before the chart's
// answer arrives, so it cannot read that answer. Keep these three in step with the builder's
// hasReading(), MetricCardContractBuilder::projectedPeriod() and periodBefore().
const hasReading = (cell) => cell?.raw_value !== null && cell?.raw_value !== undefined
  && cell.raw_value !== '' && !Number.isNaN(Number(cell.raw_value));

// A projected week starts on the workspace's day while the cells key every week by its ISO Monday,
// so the projection belongs to the cell whose week is within three days of it.
const drawnPeriodOf = (startDate, interval, cells) => {
  if (interval !== 'weekly') return startDate;
  const at = Date.parse(`${startDate}T00:00:00Z`);
  return Object.keys(cells).find((period) => Math.abs(Date.parse(`${period}T00:00:00Z`) - at) <= 3 * MS_PER_DAY) ?? startDate;
};

// By the calendar, not the cells' keys: a period with no row has no key, and the key before it
// would be an older period with a reading.
const periodBefore = (period, interval) => {
  const [year, month, day] = period.split('-').map(Number);
  const before = {
    daily: [year, month - 1, day - 1],
    weekly: [year, month - 1, day - 7],
    monthly: [year, month - 2, day],
    quarterly: [year, month - 4, day],
  }[interval] ?? [year - 1, month - 1, day];
  return new Date(Date.UTC(...before)).toISOString().slice(0, 10);
};

/**
 * @param {object} config
 * @param {string} config.chartId  The id of the drawer's <x-charts.superset-chart>.
 * @param {object} config.text     Translated strings from Blade: chartTitle (':metric'),
 *                                 and actual, projected and goal, the tooltip's row names.
 */
export default (config = {}) => {
  const chartId = String(config.chartId ?? '');
  const text = config.text ?? {};

  // Kept in the factory closure, not as reactive properties: nothing in Blade reads them, and
  // Alpine would deep-proxy every contract the server sends.
  // `chart` is drawerChart()'s latest answer, whose pictures the goal toggles switch between.
  let chart = null;
  // Bumped for every question asked of the server, so an answer to an earlier one is dropped.
  let requestToken = 0;
  let refreshQueued = false;
  let unhookFailure = null;

  return {

    // -------------------------------------------------------------------------
    // Data properties
    // -------------------------------------------------------------------------

    showChartControls: false,
    cachedMetricData: null,
    cachedLastValueAndDelta: null,
    cachedChartData: null,

    // -------------------------------------------------------------------------
    // Lifecycle - init() is auto-called by Alpine.js
    // -------------------------------------------------------------------------

    init() {
      // A failed Livewire call never settles the promise it returned, so the failure is read off
      // the request instead, and the host shows its failed state with Retry.
      unhookFailure = this.$wire.$hook('commit', ({ commit, fail }) => {
        if (!commit.calls.some((call) => call.method === 'drawerChart')) return;

        const token = requestToken;
        fail(() => {
          if (token === requestToken) this.mdd_dispatchChart({ status: 'failed' });
        });
      });

      this.mdd_refreshAll();

      this.$watch('drawerSelectedMetricId', () => this.mdd_refreshAll());
      this.$watch('activeInterval', () => this.mdd_refreshAll());
      // The latest column decides whether the projection is drawn, which only the server builds.
      this.$watch('currentPeriodColumnState', () => this.mdd_refreshAll());
      this.$watch(() => this.activeTab?.id, () => this.mdd_refreshAll());
      // Watch specific date_range properties instead of JSON.stringify
      this.$watch(() => this.activeTab?.settings?.date_range?.start, () => this.mdd_refreshAll());
      this.$watch(() => this.activeTab?.settings?.date_range?.end, () => this.mdd_refreshAll());
      // Watch goal for current interval instead of all goals
      this.$watch(() => this.drawerSelectedMetricMeta?.goals?.[this.activeInterval]?.goal_value, () => this.mdd_refreshAll());
      this.$watch(() => this.drawerSelectedMetricMeta?.goals?.[this.activeInterval]?.goal_criteria, () => this.mdd_refreshAll());
    },

    destroy() {
      unhookFailure?.();
      unhookFailure = null;
      requestToken++;
      chart = null;
    },

    // -------------------------------------------------------------------------
    // Refresh orchestration
    // -------------------------------------------------------------------------

    /**
     * One refresh per tick: switching tabs moves the tab, its interval, its latest column and
     * its dates at once, and each watcher firing its own refresh would ask the server four times.
     */
    mdd_refreshAll() {
      if (refreshQueued) return;
      refreshQueued = true;

      this.$nextTick(() => {
        refreshQueued = false;
        this.mdd_updateMetricDataCache();
        this.mdd_updateDeltaCache();
        this.mdd_updateChartDataCache();
        this.mdd_requestChart();
      });
    },

    // -------------------------------------------------------------------------
    // Goal helpers
    // -------------------------------------------------------------------------

    mdd_getGoalData() {
      return this.drawerSelectedMetricMeta?.goals?.[this.activeInterval] ?? null;
    },

    mdd_hasGoalForInterval() {
      const goal = this.mdd_getGoalData();
      return goal && goal.goal_value !== null && goal.goal_value !== undefined;
    },

    // -------------------------------------------------------------------------
    // Tooltip / header helpers
    // -------------------------------------------------------------------------

    mdd_getValueTooltipHeaderInfo() {
      if (!this.cachedLastValueAndDelta?.date) return null;
      const headers = this.displayHeaders || [];
      return headers.find(h => h.start_date === this.cachedLastValueAndDelta.date) ?? null;
    },

    // -------------------------------------------------------------------------
    // Date range / projected helpers
    // -------------------------------------------------------------------------

    mdd_getFilteredDateRange() {
      // Use parent's tc_getDateRange if available (respects tab date range settings)
      if (typeof this.tc_getDateRange === 'function') {
        return this.tc_getDateRange();
      }
      return null;
    },

    mdd_isProjectedInDateRange() {
      const meta = this.drawerSelectedMetricMeta;
      const interval = this.activeInterval;

      if (!meta?.projected?.[interval]) {
        return false;
      }

      const projectedStartDate = meta.projected[interval].start_date;
      if (!projectedStartDate) {
        return false;
      }

      const dateRange = this.mdd_getFilteredDateRange();
      if (!dateRange || !dateRange.end) {
        return true; // No filter, allow projected
      }

      // Projected is valid only if its start date is within the filtered range
      return projectedStartDate <= dateRange.end;
    },

    mdd_shouldUseProjected() {
      return (this.currentPeriodColumnState === 'projected' || this.currentPeriodColumnState === 'both') && this.mdd_isProjectedInDateRange();
    },

    // -------------------------------------------------------------------------
    // Period end date (used for overlap filtering)
    // -------------------------------------------------------------------------

    mdd_getPeriodEndDate(startDateStr, interval) {
      // Given a period start_date (YYYY-MM-DD) and interval, return the end date string (YYYY-MM-DD)
      const parts = startDateStr.split('-');
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // 0-indexed
      const day = parseInt(parts[2]);
      let endDate;

      switch (interval) {
        case 'daily':
          endDate = new Date(year, month, day);
          break;
        case 'weekly':
          endDate = new Date(year, month, day + 6);
          break;
        case 'monthly':
          endDate = new Date(year, month + 1, 0); // last day of month
          break;
        case 'quarterly':
          endDate = new Date(year, month + 3, 0); // last day of quarter
          break;
        case 'yearly':
          endDate = new Date(year, 11, 31); // Dec 31
          break;
        default:
          endDate = new Date(year, month, day);
      }

      const yy = endDate.getFullYear();
      const mm = String(endDate.getMonth() + 1).padStart(2, '0');
      const dd = String(endDate.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    },

    // -------------------------------------------------------------------------
    // Metric data fetching / caching
    // -------------------------------------------------------------------------

    mdd_getMetricDataForInterval() {
      const metricId = this.drawerSelectedMetricId;
      const interval = this.activeInterval;

      if (!metricId) return null;

      const fullData = this.fullScorecardData;
      if (!fullData?.metrics) return null;

      const metricData = fullData.metrics[metricId];
      if (!metricData) return null;

      const intervalData = metricData.data?.[interval];
      if (!intervalData) return null;

      // Filter by date range if tab settings specify one
      const dateRange = this.mdd_getFilteredDateRange();
      if (!dateRange || !dateRange.start || !dateRange.end) {
        return intervalData;
      }

      // Use overlap logic to match what the table header generator does:
      // A period is included if any part of it overlaps the date range.
      const filtered = {};
      Object.keys(intervalData).forEach(startDate => {
        const endDate = this.mdd_getPeriodEndDate(startDate, interval);
        if (startDate <= dateRange.end && endDate >= dateRange.start) {
          filtered[startDate] = intervalData[startDate];
        }
      });

      return Object.keys(filtered).length > 0 ? filtered : null;
    },

    mdd_updateMetricDataCache() {
      this.cachedMetricData = this.mdd_getMetricDataForInterval();
    },

    // -------------------------------------------------------------------------
    // Last value & delta
    // -------------------------------------------------------------------------

    mdd_getLastValueAndDelta() {
      const formattedCells = this.cachedMetricData;

      if (!formattedCells || Object.keys(formattedCells).length === 0) {
        return null;
      }

      const dates = Object.keys(formattedCells).sort();

      // Get first data point
      const firstDate = dates[0];
      const firstData = formattedCells[firstDate];

      if (!firstData || firstData.raw_value === null || firstData.raw_value === undefined) {
        return null;
      }

      // Determine last data point - use projected only if setting allows it
      const meta = this.drawerSelectedMetricMeta;
      const interval = this.activeInterval;
      let lastValue, lastFormattedValue, lastDate;

      if (this.mdd_shouldUseProjected() && meta?.projected?.[interval]) {
        const projectedObj = meta.projected[interval];
        lastValue = projectedObj.value;
        if (lastValue === undefined || lastValue === null) {
          lastValue = projectedObj.formatted_value;
        }
        lastFormattedValue = projectedObj.formatted_value;
        lastDate = projectedObj.start_date;
      } else {
        // Use last actual data point
        const lastActualDate = dates[dates.length - 1];
        const lastData = formattedCells[lastActualDate];

        if (!lastData || lastData.raw_value === null || lastData.raw_value === undefined) {
          return null;
        }

        lastValue = lastData.raw_value;
        lastFormattedValue = lastData.formatted_value;
        lastDate = lastActualDate;
      }

      if (lastValue === null || lastValue === undefined) {
        return null;
      }

      const result = {
        value: lastValue,
        formattedValue: lastFormattedValue,
        date: lastDate,
        delta: null,
        deltaPercentage: null,
        deltaFormatted: null,
        deltaIsGood: null,
        firstValue: firstData.raw_value,
        firstFormattedValue: firstData.formatted_value,
        firstDate: firstDate
      };

      // Calculate delta between first and last
      const currentValue = parseFloat(lastValue);
      const firstValue = parseFloat(firstData.raw_value);

      if (!isNaN(currentValue) && !isNaN(firstValue) && firstValue !== 0) {
        const delta = currentValue - firstValue;
        const deltaPercentage = ((delta / Math.abs(firstValue)) * 100);

        result.delta = delta;
        result.deltaPercentage = deltaPercentage;
        result.deltaFormatted = (deltaPercentage >= 0 ? '+' : '') + deltaPercentage.toFixed(2) + '%';

        // The arrow reports direction (a fact: the value rose or fell); the colour reports whether
        // that direction is good for THIS metric. Higher-is-better is the default (a null/unset
        // goal direction reads as higher-is-better, matching goal_criteria's own default); a
        // less_than_or_equal metric flips it, so a decrease reads as an improvement (green).
        const higherIsBetter = metricHigherIsBetter(this.drawerSelectedMetricMeta?.goal_criteria);
        result.deltaIsGood = deltaPercentage === 0 ? null : ((deltaPercentage > 0) === higherIsBetter);
      }

      return result;
    },

    mdd_updateDeltaCache() {
      this.cachedLastValueAndDelta = this.mdd_getLastValueAndDelta();
    },

    // -------------------------------------------------------------------------
    // Chart section data (the header badge and the labels under the chart)
    // -------------------------------------------------------------------------

    mdd_updateChartDataCache() {
      this.cachedChartData = this.mdd_getChartData();
    },

    /**
     * The periods in range that have a reading, plus the projected period while the chart draws
     * it, and the first and last reading's names as the scorecard's columns print them. Null with
     * fewer than two readings, which hides the whole chart section, headline included;
     * drawerChart() declines to draw such a metric for the same reason.
     */
    mdd_getChartData() {
      const cells = this.cachedMetricData;
      if (!cells) return null;

      const dates = Object.keys(cells)
        .filter((date) => cells[date] && cells[date].raw_value !== null && cells[date].raw_value !== undefined)
        .sort();

      if (dates.length < 2) return null;

      const labels = [...dates];
      const projectedDate = this.mdd_getDrawnProjectedDate();
      if (projectedDate && !labels.includes(projectedDate)) {
        labels.push(projectedDate);
      }

      const formattedLabels = labels.map((date) => this.mdd_formatDateSimple(this.mdd_parseDateLocal(date)));

      return {
        labels,
        customLabels: this.mdd_buildCustomLabels(dates[0], dates[dates.length - 1], formattedLabels),
      };
    },

    /**
     * The projected period's start, or null. Counted by the time-period badge only while the
     * chart draws the projection, so the badge spans what is on screen. The chart draws it only
     * from a reading in the period just before it, as the drawer always has.
     */
    mdd_getDrawnProjectedDate() {
      if (!this.mdd_shouldUseProjected()) return null;

      const interval = this.activeInterval;
      const projected = this.drawerSelectedMetricMeta?.projected?.[interval];
      const value = parseFloat(projected?.value ?? projected?.formatted_value);
      if (Number.isNaN(value)) return null;

      const cells = this.cachedMetricData ?? {};
      const before = periodBefore(drawnPeriodOf(projected.start_date, interval, cells), interval);

      return hasReading(cells[before]) ? projected.start_date : null;
    },

    mdd_buildCustomLabels(firstDate, lastDate, formattedLabels) {
      const headers = this.cached_scorecard_headers || [];
      const firstHeader = headers.find(h => h.start_date === firstDate);
      const lastHeader = headers.find(h => h.start_date === lastDate);
      return [
        {
          label: firstHeader?.label || formattedLabels[0],
          sub_label: firstHeader?.sub_label || ''
        },
        {
          label: lastHeader?.label || formattedLabels[formattedLabels.length - 1],
          sub_label: lastHeader?.sub_label || ''
        }
      ];
    },

    // -------------------------------------------------------------------------
    // The sparkline (drawn by <x-charts.superset-chart>)
    // -------------------------------------------------------------------------

    /**
     * Ask the server for the sparkline as the tab shows it: every picture the goal toggles can
     * show, in one answer. The tab's latest column and dates are the page's, not the saved tab's,
     * because the tab drawer applies an edit before it is saved.
     */
    async mdd_requestChart() {
      const token = ++requestToken;
      chart = null;

      const metricId = this.drawerSelectedMetricId;

      // No metric, or fewer than two readings: the section is hidden and there is nothing to ask.
      // The host is still emptied, or it would keep the previous metric's chart (or a first
      // open's skeleton) under this metric's name, and hold that contract for a later mount.
      if (!metricId || !this.activeInterval || !this.cachedChartData) {
        this.mdd_dispatchChart({ status: 'empty' });
        return;
      }

      this.mdd_dispatchChart({ status: 'loading' });

      const range = this.mdd_getFilteredDateRange();
      const response = await this.$wire.drawerChart(
        metricId,
        this.activeInterval,
        this.currentPeriodColumnState,
        range?.start && range?.end ? { start: range.start, end: range.end } : null,
      );

      if (token !== requestToken) return;

      chart = response ?? null;

      if (!chart) {
        this.mdd_dispatchChart({ status: 'empty' });
        return;
      }

      this.mdd_sendChart();
    },

    /**
     * Hand the host the picture the toggles ask for, with no request: the goal line, and the
     * goal's coloured fill on top of it. The dashes and the fill travel as the contract's series
     * styles and are drawn by the bundle's styling hook (chart-contract.md), in the colours the
     * drawer has always used, which come from the app's palette rather than the contract: the goal
     * in grey, whatever the workspace's palette, and the bands in their good and bad tones.
     */
    mdd_sendChart() {
      if (!chart) return;

      const { contracts, labels, displays, goal } = chart;
      const drawsGoal = Boolean(this.metricChartShowGoalLine && contracts.withGoal);
      const fillsGoal = drawsGoal && Boolean(this.metricChartShowColoredFill && contracts.withGoalBands);
      const contract = fillsGoal ? contracts.withGoalBands : drawsGoal ? contracts.withGoal : contracts.withoutGoal;
      // Each period by the scorecard's own name for it, in the tooltip's header and the host's
      // sr-only table alike, so neither calls a month's reading by its first day ("Sep 1, 2026").
      // The grain names any period the scorecard has no header for.
      const periods = this.mdd_periodNames((contract.queriesData?.[0]?.data ?? []).map((row) => row.__timestamp));
      const grain = contract.formData?.timeGrainSqla ?? null;
      this.mdd_dispatchChart({
        contract,
        periods,
        styleColors: {
          series: labels.goal ? { [labels.goal]: GOAL_LINE_COLOR } : {},
          goalBand: { good: GOAL_BAND_GOOD_COLOR, bad: GOAL_BAND_BAD_COLOR },
        },
        title: this.mdd_chartTitle(labels.actual),
        // The host's sr-only table and accessible name call each series what its tooltip row
        // does. Its label stays the series' key, which the tooltip finds its rows by.
        seriesNames: Object.fromEntries([
          [labels.actual, text.actual],
          [labels.projected, text.projected],
          [labels.goal, text.goal],
        ].filter(([label, seriesName]) => label && seriesName)),
        tooltip: {
          ...TOOLTIP_CHROME,
          trigger: 'axis',
          // The footer measures the reading against the goal only while the goal line is drawn,
          // so the tooltip never cites a line the reader cannot see.
          formatter: supersetTooltipFormatter(
            { labels, displays, goal: drawsGoal ? goal : null },
            { locale: document.documentElement.lang || undefined, text, grain, periods },
          ),
        },
      });
    },

    /**
     * instant => the name the scorecard's column header gives that period at the active interval
     * ("September 2026", "Week 36, Sep 1 - Sep 7"). A period with no column, such as a projected
     * one past the tab's dates, is named by the scorecard's own header builder.
     */
    mdd_periodNames(instants) {
      const interval = this.activeInterval;
      const headerFor = typeof this.createHeaderForDate === 'function'
        ? (startDate) => this.createHeaderForDate(startDate, interval, this.firstDayOfWeek)
        : null;

      return scorecardPeriodNames(instants, interval, this.cached_scorecard_headers || [], headerFor);
    },

    mdd_dispatchChart(detail) {
      // The host names its announcements and table after the title it last received, and a drawn
      // chart used to be the only thing carrying one. A status sent right after a metric switch
      // therefore named the previous metric, so every status names the metric open now.
      const title = 'status' in detail && !detail.title ? this.mdd_chartTitle() : null;
      window.dispatchEvent(new CustomEvent(CHART_DATA_EVENT, { detail: { id: chartId, ...(title ? { title } : {}), ...detail } }));
    },

    /** The row's name when the drawer was opened from a row, else the series' own label. */
    mdd_chartTitle(fallback = null) {
      const name = this.drawerSelectedRowMeta?.display_name || fallback;

      return name ? (text.chartTitle ?? ':metric').replace(':metric', name) : null;
    },

    /** The host's Retry after a failure the server reported: it holds no contract to redraw. */
    mdd_retryChart(event) {
      if (event.detail?.id !== chartId) return;

      this.mdd_requestChart();
    },

    // -------------------------------------------------------------------------
    // Date formatting utilities
    // -------------------------------------------------------------------------

    mdd_formatDateSimple(date) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    mdd_parseDateLocal(dateStr) {
      // Parse date string as local time to avoid timezone shifts
      // Input format: YYYY-MM-DD
      const parts = dateStr.split('-');
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    },

    // -------------------------------------------------------------------------
    // Time period label for the chart header badge
    // -------------------------------------------------------------------------

    mdd_getTimePeriodLabel() {
      if (!this.cachedChartData || !this.cachedChartData.labels || this.cachedChartData.labels.length < 2) {
        return null;
      }

      const dates = this.cachedChartData.labels.filter(d => d);
      if (dates.length < 2) return null;

      const firstDate = this.mdd_parseDateLocal(dates[0]);
      const lastDate = this.mdd_parseDateLocal(dates[dates.length - 1]);

      const diffTime = Math.abs(lastDate - firstDate);
      const diffDays = Math.ceil(diffTime / MS_PER_DAY);

      // Use parent's cached flag to determine if current period is included
      // This is computed in view-v2 updateHeaders() using isHeaderCurrentPeriod()
      const includesCurrent = this.cached_last_is_current_period || false;

      if (diffDays < 7) {
        if (diffDays === 1) {
          return includesCurrent ? 'Last day' : '1 day';
        }
        return includesCurrent ? `Last ${diffDays} days` : `${diffDays} days`;
      } else if (diffDays < 28) {
        const weeks = Math.round(diffDays / 7);
        if (weeks === 1) {
          return includesCurrent ? 'Last week' : '1 week';
        }
        return includesCurrent ? `Last ${weeks} weeks` : `${weeks} weeks`;
      } else if (diffDays < 365) {
        const months = Math.round(diffDays / 30);
        if (months === 1) {
          return includesCurrent ? 'Last month' : '1 month';
        }
        return includesCurrent ? `Last ${months} months` : `${months} months`;
      } else {
        const years = Math.round(diffDays / 365);
        if (years === 1) {
          return includesCurrent ? 'Last year' : '1 year';
        }
        return includesCurrent ? `Last ${years} years` : `${years} years`;
      }
    },

  };
};
